import {
    calendarsAreNotAvailable,
    tryAgainByRefreshingBrowser,
    tryAgainByRefreshingBrowserAfterSomeTime,
    someCalendarsAreNotAvailable,
    loadCalendarAccountsError,
    combinedErrorMessageFormat,
    updateConnectedAccounts,
} from './getErrorMessage.locstring.json';
import loc, { format } from 'owa-localize';
import { assertNever } from 'owa-assert';

export enum FailureType {
    Failed,
    Throttled,
    FailedAccount,
}

/**
 * Get the refresh suggestion for the failure type. This can be used regardless of whether or not we were
 * able to load events to advise the user on how to mitigate the problem.
 * @param failureType
 */
export function getRefreshSuggestionMessage(failureType: FailureType): string {
    switch (failureType) {
        case FailureType.Throttled:
            return loc(tryAgainByRefreshingBrowserAfterSomeTime);
        case FailureType.Failed:
            return loc(tryAgainByRefreshingBrowser);
        case FailureType.FailedAccount:
            return loc(updateConnectedAccounts);
        default:
            return assertNever(failureType);
    }
}

/**
 * Get the load error to display to the user when we are unable to load any events. In this case, the error message
 * is the same for all failure types.
 */
export function getPrimaryErrorMessage(): string {
    return loc(calendarsAreNotAvailable);
}

/**
 * Get the partial load error to display to the user when we were able to load some events.
 * @param failureType
 */
export function getPartialErrorMessage(failureType: FailureType) {
    return failureType === FailureType.FailedAccount
        ? loc(loadCalendarAccountsError)
        : loc(someCalendarsAreNotAvailable);
}

/**
 * Get the partial error message and the refresh suggestion for the failure type in a combined string. This
 * string can be shown to the user when we were able to load some events.
 * @param failureType
 */
export function getErrorMessageAndRefreshSuggestion(failureType: FailureType) {
    return format(
        loc(combinedErrorMessageFormat),
        getPartialErrorMessage(failureType),
        getRefreshSuggestionMessage(failureType)
    );
}
